import * as React from "react";
import { getLocale, setLocale, locales } from "~/paraglide/runtime";
import { LOCALE_NAMES, type SupportedLocale } from "~/lib/locale";
import { getLocaleSwitchTarget } from "~/lib/locale-switch";

export function LanguageSwitcher() {
  const [open, setOpen] = React.useState(false);
  const [activeDescendant, setActiveDescendant] = React.useState(-1);
  const ref = React.useRef<HTMLDivElement>(null);
  const listboxRef = React.useRef<HTMLUListElement>(null);
  const currentLocale = getLocale() as SupportedLocale;

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveDescendant(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (open && listboxRef.current) {
      listboxRef.current.focus();
    }
  }, [open]);

  function selectLocale(locale: (typeof locales)[number]) {
    const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const target = getLocaleSwitchTarget(currentHref, locale);
    document.cookie = `ALCHEMA_LOCALE=${locale};path=/;max-age=31536000;SameSite=Lax;Secure`;
    setLocale(locale, { reload: false });
    setOpen(false);
    setActiveDescendant(-1);
    window.location.assign(target);
  }

  const openedViaKeyboard = React.useRef(false);

  function handleButtonKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openedViaKeyboard.current = true;
      setOpen(true);
      setActiveDescendant(locales.indexOf(currentLocale));
    }
  }

  function handleListboxKeyDown(e: React.KeyboardEvent) {
    const count = locales.length;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveDescendant((prev) => (prev + 1) % count);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveDescendant((prev) => (prev - 1 + count) % count);
        break;
      case "Home":
        e.preventDefault();
        setActiveDescendant(0);
        break;
      case "End":
        e.preventDefault();
        setActiveDescendant(count - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (activeDescendant >= 0 && activeDescendant < count) {
          selectLocale(locales[activeDescendant]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setActiveDescendant(-1);
        break;
    }
  }

  const activeOptionId = activeDescendant >= 0 ? `lang-option-${locales[activeDescendant]}` : undefined;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          if (openedViaKeyboard.current) {
            openedViaKeyboard.current = false;
            return;
          }
          const next = !open;
          setOpen(next);
          if (next) setActiveDescendant(locales.indexOf(currentLocale));
          else setActiveDescendant(-1);
        }}
        onKeyDown={handleButtonKeyDown}
        className="btn-ghost flex h-11 items-center gap-1.5 rounded-lg px-2 text-xs font-medium uppercase tracking-wide touch-manipulation"
        style={{ color: "inherit" }}
        aria-label="Switch language"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        {currentLocale.toUpperCase()}
      </button>

      {open && (
        <ul
          ref={listboxRef}
          role="listbox"
          tabIndex={0}
          aria-label="Select language"
          aria-activedescendant={activeOptionId}
          onKeyDown={handleListboxKeyDown}
          className="absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-[10px] border py-1 outline-none"
          style={{
            backgroundColor: "var(--surface)",
            borderColor: "var(--border)",
            boxShadow: "0 4px 12px oklch(0 0 0 / 0.08)",
          }}
        >
          {locales.map((locale, idx) => (
            <li
              key={locale}
              id={`lang-option-${locale}`}
              role="option"
              tabIndex={-1}
              aria-selected={locale === currentLocale}
              onClick={() => selectLocale(locale)}
              onPointerUp={(e) => {
                e.preventDefault();
                selectLocale(locale);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectLocale(locale);
                }
              }}
              onMouseEnter={() => setActiveDescendant(idx)}
              className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
              style={{
                color: locale === currentLocale ? "var(--accent)" : "var(--text)",
                fontWeight: locale === currentLocale ? 600 : 400,
                backgroundColor:
                  idx === activeDescendant
                    ? locale === currentLocale
                      ? "var(--accent-subtle)"
                      : "var(--surface-raised)"
                    : locale === currentLocale
                      ? "var(--accent-subtle)"
                      : "transparent",
              }}
            >
              <span className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)", width: "1.5rem" }}>
                {locale}
              </span>
              <span>{LOCALE_NAMES[locale as SupportedLocale] ?? locale}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
